import axios from "@/config/axios";
import microsoftInstance, {
  initializeMicrosoft,
  microsoftConfigured,
  microsoftLoginRequest,
} from "@/config/microsoft";
import { checkRequiresAttentionToast } from "@/helpers/orderingFacilityToast";

let authenticationInitialized = false;

// initial state
const initialState = () => {
  return {
    user: null,
    authenticationStatus: null,
    authenticationDetails: {},
    passwordUpdateStatus: null,
    generalErrorMessage: null,
    forgotPasswordStatus: {},
    forgotPasswordVerificationStatus: null,
    userRequestedActionTime: null,
    userPrimaryLabId: null,
    userAllowedLabs: {},
    userLabDetails: {},
    hasShownAttentionToast: false, // New flag to prevent repeated toasts
  }
}
   
const state = initialState()

const getters = {
  getAuthenticatedUser: (state) => state.user,
  getAuthenticationStatus: (state) => state.authenticationStatus,
  getAuthenticationDetails: (state) => state.authenticationDetails,
  getGeneralErrorMessage: (state) => state.generalErrorMessage,
  getPasswordUpdateStatus: (state) => state.passwordUpdateStatus,
  getForgotPasswordStatus: (state) => state.forgotPasswordStatus,
  getForgotPasswordVerificationStatus: (state) =>
    state.forgotPasswordVerificationStatus,
  getUserRequestedActionTime: (state) => state.userRequestedActionTime,
  getUserPrimaryLabId: (state) => state.userPrimaryLabId,
  getUserAllowedLabs: (state) => state.userAllowedLabs,
  getUserLabDetails: (state) => state.userLabDetails,
  getHasShownAttentionToast: (state) => state.hasShownAttentionToast,
}

const mutations = {
  setAuthenticatedUser: (state, user) => {
    state.user = user
  },
  setAuthenticationStatus: (state, status) => {
    state.authenticationStatus = status
  },
  setAuthenticationDetails: (state, tokens) => {
    state.authenticationDetails = tokens
  },
  setPasswordUpdateStatus: (state, message) => {
    state.passwordUpdateStatus = message
  },
  setForgotPasswordStatus: (state, data) => {
    state.forgotPasswordStatus = data
  },
  setForgotPasswordVerificationStatus: (state, status) => {
    state.forgotPasswordVerificationStatus = status
  },
  setUserRequestedActionTime: (state, time) => {
    state.userRequestedActionTime = time
  },
  setUserPrimaryLabId(state, data) {
    state.userPrimaryLabId = data
  },
  setUserAllowedLabs(state, data) {
    state.userAllowedLabs = data
  },
  setUserLabDetails(state, data) {
    state.userLabDetails = data
  },
  setHasShownAttentionToast: (state, value) => {
    state.hasShownAttentionToast = value;
  },
  reset: (state) => {
    const initial = initialState()
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key]
    })
  },
}

const actions = {
  initializeAuthentication: async ({ dispatch, commit, state, rootGetters }) => {
    if (authenticationInitialized) return null;
    authenticationInitialized = true;

    try {
      if (!microsoftConfigured) return null;
      await initializeMicrosoft();
      const redirectResponse = await microsoftInstance.handleRedirectPromise();
      if (!redirectResponse) return null;
      if (!redirectResponse.idToken) {
        throw new Error("Microsoft ID token missing");
      }

      const response = await axios.post("/microsoft", {
        idToken: redirectResponse.idToken,
      });
      const applicationToken = response.data.access_token || response.data.token;
      const applicationUser = response.data.user_details || response.data.user;
      if (!applicationToken || !applicationUser) {
        throw new Error("Backend Microsoft authentication response is incomplete");
      }

      const userDetails = {
        ...applicationUser,
        provider: "microsoft",
      };
      const tokens = {
        access_token: applicationToken,
        refresh_token: response.data.refresh_token,
      };

      axios.defaults.headers.common.Authorization = applicationToken;
      commit("setAuthenticatedUser", userDetails);
      commit("setAuthenticationDetails", tokens);
      commit("setAuthenticationStatus", true);
      commit("setUserPrimaryLabId", userDetails.lab);
      commit("setUserAllowedLabs", userDetails.labs);
      commit("setUserLabDetails", userDetails.labs);
      await dispatch("session/updateSessionStatus", true, { root: true });

      const redirect = window.sessionStorage.getItem("microsoft_redirect");
      window.sessionStorage.removeItem("microsoft_redirect");
      let authenticationDestination;
      if (redirect) {
        authenticationDestination = JSON.parse(redirect);
      } else if (userDetails.user_type === "TOP_DOG") {
        authenticationDestination = "/labs";
      } else {
        authenticationDestination = "/all-cases";
      }

      try {
        await checkRequiresAttentionToast({
          userDetails,
          state,
          commit,
          dispatch,
          rootGetters,
        });
      } catch (toastError) {
        console.warn("Microsoft login attention toast failed:", toastError);
      }

      return authenticationDestination;
    } catch (error) {
      console.error("Microsoft authentication initialization failed:", error);
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
      return null;
    }
  },

  microsoftSignIn: async () => {
    if (!microsoftConfigured) {
      throw new Error("Microsoft SSO is not configured");
    }
    const loginQuery = new URLSearchParams(window.location.search);
    const redirectName = loginQuery.get("redirect");
    if (redirectName) {
      const redirectParams = loginQuery.get("params");
      const redirectQuery = loginQuery.get("query");
      window.sessionStorage.setItem(
        "microsoft_redirect",
        JSON.stringify({
          name: redirectName,
          params: redirectParams ? JSON.parse(redirectParams) : {},
          query: redirectQuery ? JSON.parse(redirectQuery) : {},
        })
      );
    } else {
      window.sessionStorage.removeItem("microsoft_redirect");
    }
    await initializeMicrosoft();
    await microsoftInstance.loginRedirect(microsoftLoginRequest);
  },

  switchRole: async ({ commit, dispatch, state }, role) => {
    if (!role || !state.user?.roles?.includes(role)) {
      throw new Error("User is not assigned to this role");
    }

    const response = await axios.post("/switch-role", { role });
    const applicationToken = response.data.access_token || response.data.token;
    const applicationUser = response.data.user_details || response.data.user;
    if (!applicationToken || !applicationUser) {
      throw new Error("Role switch response is incomplete");
    }

    const userDetails = {
      ...applicationUser,
      provider: state.user.provider,
      activeRole: role,
      user_type: role,
    };
    const tokens = {
      access_token: applicationToken,
      refresh_token: response.data.refresh_token,
    };

    // Install the new application token before any role-dependent request starts.
    axios.defaults.headers.common.Authorization = applicationToken;
    commit("setAuthenticatedUser", userDetails);
    commit("setAuthenticationDetails", tokens);
    commit("setAuthenticationStatus", true);
    commit("setUserPrimaryLabId", userDetails.lab);
    commit("setUserAllowedLabs", userDetails.labs);
    commit("setUserLabDetails", userDetails.labs);
    await dispatch("roleSwitchReset", [], { root: true });
    await dispatch("session/updateSessionStatus", true, { root: true });
    await dispatch("profile/getProfile", null, { root: true });

    return userDetails;
  },

  signOut: async ({ commit, dispatch }) => {
    dispatch("errors/clear", [], { root: true });
    commit("auth/reset", null, { root: true });
    commit("auth/resetProfileData", null, { root: true });
    commit("auth/setHasShownAttentionToast", false, { root: true });
  },

  signin: async ({ commit, dispatch, rootGetters, state }, params) => {
    try {
      dispatch("errors/clear", [], { root: true })

      commit("auth/reset", null, { root: true })
      let response = await axios.post("/signin", params)
      let userDetails = response.data.user_details
      let tokens = {
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
      }
      commit("auth/setAuthenticatedUser", userDetails, { root: true })

      commit("auth/setAuthenticationDetails", tokens, { root: true })

      commit("auth/setAuthenticationStatus", true, { root: true })

      commit("auth/setUserPrimaryLabId", userDetails.lab, { root: true })

      commit("auth/setUserAllowedLabs", userDetails.labs, { root: true })

      commit("auth/setUserLabDetails", userDetails.labs, { root: true })
    // toast logic here
    await checkRequiresAttentionToast({
      userDetails,
      state,
      commit,
      dispatch,
      rootGetters,
    });

    } catch (error) {
      console.log(error)
      commit("auth/setAuthenticatedUser", null, { root: true })
      commit("auth/setAuthenticationDetails", null, { root: true })
      commit("auth/setAuthenticationStatus", false, { root: true })
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true })
      }
    }
  },

  logLocation: async (_, params) => {
    try {
      await axios.post("/log-location", params);
    } catch {
      // non-critical — ignore failures silently
    }
  },

  reset: function (context) {
    context.dispatch("errors/clear", [], { root: true })
    context.commit("auth/reset", null, { root: true })
  },
  updatePassword: async ({ commit, dispatch }, params) => {
    try {
      dispatch("errors/clear", [], { root: true })
      commit("auth/setPasswordUpdateStatus", null, {
        root: true,
      })
      let response = await axios.patch("/password/update", params)
      commit("auth/setPasswordUpdateStatus", response.data, {
        root: true,
      })
    } catch (error) {
      commit("auth/setPasswordUpdateStatus", null, {
        root: true,
      })
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
  forgotPassword: async ({ commit, dispatch }, params) => {
    try {
      commit("errors/clear", null, { root: true })
      commit("setForgotPasswordStatus", null)
      let response = await axios.post("/forgot-password", params)
      commit("setForgotPasswordStatus", response.data)
    } catch (err) {
      await dispatch("errors/errorResponse", err.response, {
        root: true,
      })
      console.log(err)
    }
  },
  forgotPasswordVerify: async ({ commit, dispatch }, params) => {
    try {
      commit("errors/clear", null, { root: true })
      commit("setForgotPasswordVerificationStatus", null)
      let response = await axios.post("/forgot-password/verify", params)
      commit("setForgotPasswordVerificationStatus", response.data)
    } catch (err) {
      await dispatch("errors/errorResponse", err.response, {
        root: true,
      })
      console.log(err)
    }
  },
  setPrimaryLabDetails: async ({ commit, dispatch }, data) => {
    try {
      commit("setUserPrimaryLabId", data._id)
      commit("setUserLabDetails", data)
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
